import { IonAlert, IonButton } from '@ionic/react';
import RCForm, { useForm } from 'rc-field-form';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import Field from '@components/Form/Field';
import InputField from '@components/Form/InputField';
import Password from '@components/Form/Password';
import Product from '@custom-types/Product';
import RoutePath from '@custom-types/RoutePath';
import useKeyboardFeedback from '@hooks/useKeyboardFeedback';
import useLogin, { LoginType } from '@hooks/useLogin';
import { generateRoute, getProduct } from '@services/navigation-service';
import { getIsOnline } from '@state/system';

import Layout from './Layout';

const Button = styled(IonButton)`
  width: 100%;
  padding: 0 16px;
  margin-top: 32px;
`;

const Form = styled(RCForm)`
  width: 100%;
`;

interface LoginProps extends Partial<RouteComponentProps<{ email?: string }>> {
  type: LoginType;
}

const Login = ({ type, match }: LoginProps) => {
  const [form] = useForm();
  const { validateFields, getFieldValue } = form;
  const isOnline = useSelector(getIsOnline);
  const [credential, setCredential] = useState();
  const { loading: submitting, error } = useLogin(type, credential);
  const isDisabled = !isOnline || submitting;
  const product = getProduct();
  useKeyboardFeedback();

  const onFinish = async () => {
    const values = await validateFields();
    setCredential(values);
  };

  // This is used for linking between the login and forgot password pages. We need to default it to
  // undefined so that the router doesn't complain about an empty param or navigate to /:email
  const email = getFieldValue('email') || undefined;

  return (
    <Layout>
      <Form
        // skipcq: JS-0417
        onFinish={onFinish}
        form={form}
        initialValues={{ email: match?.params.email || '', password: '' }}
      >
        <Field
          name="email"
          rules={[
            { required: true, message: t`Email Address is required.` },
            { type: 'email', message: t`Invalid email address.` },
          ]}
        >
          <InputField
            label={t`Email Address`}
            inputMode="email"
            role="textbox"
            aria-label="email"
            data-testid="email"
          />
        </Field>
        {type === LoginType.WithPassword && (
          <Field name="password" rules={[{ required: true, message: t`Password is required.` }]}>
            <Password label={t`Password`} />
          </Field>
        )}
        {type === LoginType.WithAccessCode && (
          <Field
            name="accessCode"
            rules={[{ required: true, message: t`Access Code is required.` }]}
          >
            <Password label={t`Access Code`} />
          </Field>
        )}
        <Button
          type="submit"
          data-testid="login"
          role="button"
          disabled={isDisabled}
        >{t`Log In`}</Button>
        {type === LoginType.WithPassword && (
          <>
            <Button
              role="button"
              fill="clear"
              size="small"
              routerLink={generateRoute(RoutePath.ForgotPassword, { email })}
            >
              {t`Forgot Password?`}
            </Button>
            <Button
              role="button"
              fill="clear"
              size="small"
              routerLink={generateRoute(RoutePath.LoginWithCode, { email })}
            >
              {t`Log in with Access Code`}
            </Button>
          </>
        )}

        {type === LoginType.WithAccessCode && (
          <>
            <Button
              role="button"
              fill="clear"
              size="small"
              routerLink={generateRoute(RoutePath.ForgotAccessCode)}
            >
              {t`Forgot Access Code?`}
            </Button>
            {product !== Product.LeadCapture && (
              <Button
                role="button"
                fill="clear"
                size="small"
                routerLink={generateRoute(RoutePath.LoginWithPassword, { email })}
              >
                {t`Log in with Password`}
              </Button>
            )}
          </>
        )}
        <IonAlert
          isOpen={error}
          header={t`Incorrect Login`}
          message={t`Check your login information and try again.`}
          buttons={[t`OK`]}
        />
      </Form>
    </Layout>
  );
};

export default Login;
